import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    // Store the variant's featured media ID before replacing
    const variantResource = event.detail.resource;
    const featuredMediaId = variantResource?.featured_media?.id;

    this.replaceWith(newMediaGallery);

    // After replacement, select the correct slide if we have a featured media ID
    if (featuredMediaId) {
      // Wait for the new gallery to be connected and slideshow to be ready
      requestAnimationFrame(() => {
        const updatedGallery = document.querySelector('media-gallery');
        if (updatedGallery) {
          let retryCount = 0;
          const maxRetries = 20; // Maximum 1 second (20 * 50ms)
          const waitForSlideshow = () => {
            const slideshow = updatedGallery.querySelector('slideshow-component');
            if (slideshow && slideshow.select) {
              // Try to find slide by media ID (slide-id attribute)
              const slide = slideshow.querySelector(`slideshow-slide[slide-id="${CSS.escape(String(featuredMediaId))}"]`);
              if (slide) {
                slideshow.select({ id: String(featuredMediaId) }, undefined, { animate: false });
              } else {
                // Fallback: find by data-media-id
                const mediaElement = slideshow.querySelector(`[data-media-id="${CSS.escape(String(featuredMediaId))}"]`);
                if (mediaElement) {
                  const parentSlide = mediaElement.closest('slideshow-slide');
                  if (parentSlide) {
                    const slideId = parentSlide.getAttribute('slide-id');
                    if (slideId) {
                      slideshow.select({ id: slideId }, undefined, { animate: false });
                    }
                  }
                }
              }
            } else if (retryCount < maxRetries) {
              // Slideshow not ready yet, try again
              retryCount++;
              setTimeout(waitForSlideshow, 50);
            }
          };
          waitForSlideshow();
        }
      });
    }
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
